import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import TrackerTable from "@/components/TrackerTable";
import { ShoppingCart, FileText, CheckCircle2, X, Save } from "lucide-react";

const TABS = [
  { id: "needs_po", label: "Needs PO", icon: ShoppingCart },
  { id: "with_po",  label: "PO Linked", icon: CheckCircle2 },
  { id: "all",      label: "All Contracts", icon: FileText },
];

export default function PurchasingPortal() {
  const [tab, setTab] = useState("needs_po");
  const [data, setData] = useState(null);
  const [poModal, setPoModal] = useState(null); // contract for PO modal

  const load = async () => {
    try { const { data } = await api.get("/portal/purchasing"); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load portal"); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-6 text-slate-500 text-sm">Loading…</div>;
  const rows = data[tab] || [];

  return (
    <div className="p-6" data-testid="purchasing-portal-page">
      <div className="mb-6 flex items-start">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Phase 8 · Purchasing Portal</div>
          <h1 className="text-2xl font-bold text-slate-900">Purchase Orders & Contract Linkage</h1>
          <p className="text-sm text-slate-500 mt-1">
            Attach POs to executed contracts, pre-attach POs to in-flight contracts, and audit every PO change.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="pp-kpis">
        <KPI icon={ShoppingCart} label="Needs PO" value={data.counts.needs_po} tone={data.counts.needs_po > 0 ? "warn" : "ok"}/>
        <KPI icon={CheckCircle2} label="PO Linked" value={data.counts.with_po}/>
        <KPI icon={FileText} label="All Contracts" value={data.counts.all}/>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {TABS.map(t => {
          const Icon = t.icon;
          const count = data.counts?.[t.id] ?? 0;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`pp-tab-${t.id}`}
                    className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px ${
                      tab === t.id ? "border-[#005289] text-[#005289] font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}>
              <Icon className="w-4 h-4"/> {t.label}
              <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${tab === t.id ? "bg-[#005289] text-white" : "bg-slate-100 text-slate-600"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div data-testid={`pp-panel-${tab}`}>
        <TrackerTable
          rows={rows}
          onChanged={load}
          testIdPrefix={`pp-${tab}`}
          emptyText="No contracts here."
          actionColumn={(c) => (
            <button onClick={() => setPoModal(c)} data-testid={`pp-attach-po-${c.contract_number}`}
                    className="h-7 px-3 text-xs font-semibold bg-[#005289] text-white rounded hover:opacity-90">
              {c.po_number ? "Update PO" : "Attach PO"}
            </button>
          )}
        />
      </div>

      {poModal && <POAttachModal contract={poModal} onClose={() => setPoModal(null)} onSaved={() => { setPoModal(null); load(); }}/>}
    </div>
  );
}

function KPI({ icon: Icon, label, value, tone = "neutral" }) {
  const color = tone === "danger" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase text-slate-500 font-bold tracking-wider"><Icon className="w-3 h-3"/> {label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function POAttachModal({ contract, onClose, onSaved }) {
  const [po, setPo] = useState({
    po_number: contract.po_number || "",
    po_amount: contract.po_amount ?? contract.contract_amount ?? "",
    po_date: contract.po_date ? contract.po_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
    notes: contract.po_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);

  const save = async () => {
    if (!po.po_number?.trim()) { toast.error("PO number is required"); return; }
    setSaving(true);
    try {
      let po_document_id = contract.po_document_id;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "po");
        const { data } = await api.post(`/contracts/${contract.id}/upload`, fd);
        po_document_id = data?.document_id || data?.id || po_document_id;
      }
      await api.post(`/contracts/${contract.id}/po`, {
        po_number: po.po_number.trim(),
        po_amount: po.po_amount ? Number(po.po_amount) : null,
        po_date: po.po_date || null,
        po_document_id,
        notes: po.notes,
      });
      toast.success("PO attached");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attach failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onClick={onClose} data-testid="po-modal">
      <div className="bg-white rounded-md w-[520px] max-w-[90vw]" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-[#005289]"/>
          <div className="font-bold text-slate-900">Attach PO — {contract.vendor_legal_name} <span className="font-mono text-xs text-slate-400">#{contract.contract_number}</span></div>
          <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">
              <span className="font-bold text-slate-700 block mb-1">PO Number *</span>
              <input data-testid="po-number-input" value={po.po_number} onChange={e => setPo({...po, po_number: e.target.value})} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md" placeholder="e.g., PO-2026-0001"/>
            </label>
            <label className="text-xs">
              <span className="font-bold text-slate-700 block mb-1">PO Amount (USD)</span>
              <input type="number" value={po.po_amount} onChange={e => setPo({...po, po_amount: e.target.value})} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md"/>
            </label>
            <label className="text-xs">
              <span className="font-bold text-slate-700 block mb-1">PO Date</span>
              <input type="date" value={po.po_date} onChange={e => setPo({...po, po_date: e.target.value})} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md"/>
            </label>
            <label className="text-xs">
              <span className="font-bold text-slate-700 block mb-1">PO Document (PDF)</span>
              <input type="file" accept="application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="po-file-input" className="text-xs"/>
            </label>
          </div>
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Notes</span>
            <textarea rows={2} value={po.notes} onChange={e => setPo({...po, notes: e.target.value})} className="w-full p-2 text-sm border border-slate-200 rounded-md" placeholder="Optional purchasing notes"/>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-4 border border-slate-200 rounded-md text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="po-save-btn" className="h-9 px-4 rounded-md bg-[#005289] text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-1">
            <Save className="w-3 h-3"/> {saving ? "Saving..." : "Save PO"}
          </button>
        </div>
      </div>
    </div>
  );
}
